import {
  Component,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChildren
} from "@angular/core";
import { NgClass, NgForOf, NgIf, NgStyle } from "@angular/common";
import { NoDataComponent } from "../no-data/no-data.component";
import { ResizableModule } from "angular-resizable-element";
import { SvgIconComponent } from "../svg-icon/svg-icon.component";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { EditableTableHeader } from "../../models/editable-table-header.interface";
import {
  EditableTableDynamicInputComponent
} from "./editable-table-dynamic-input/editable-table-dynamic-input.component";
import { DataUpdatedOutput } from "./DataUpdatedOutput.interface";
import { CommonUtils } from "../../utils/common-utils";

@Component({
  selector: "app-editable-table",
  standalone: true,
  imports: [
    NgForOf,
    NgIf,
    NoDataComponent,
    ResizableModule,
    SvgIconComponent,
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    EditableTableDynamicInputComponent,
    NgStyle
  ],
  templateUrl: "./editable-table.component.html",
  styleUrls: ["./editable-table.component.scss"]
})
export class EditableTableComponent {
  selectedRows: any[] = [];
  selectedCols: any[] = [];
  columnValues: { [key: string]: any } = {};
  allTableRecordsSelected: boolean = false;
  searchTerm: string = "";
  loading = false;

  //Table Config
  @Input() tableHeaders: EditableTableHeader[] = [];
  @Input() tableData: any[] = [];

  //TABLE
  @Input() uniqueId: string = "id";
  @Input() disableTable: boolean = false;
  @Input() noDataFoundIcon!: string;
  @Input() noDataFoundErrorMsg: string = "No data found";
  @Input() selectedRowsInput: any[] = [];
  @Input() showInputFieldsOnTh: boolean = true;
  @Input() allowSelection: boolean = true;
  @Input() deletionAllowed: boolean = false;
  @Input() bulkDeletionAllowed: boolean = false;

  @Output() tableDataChange: EventEmitter<void> = new EventEmitter();
  @Output() addNewRecord = new EventEmitter<any>();
  @Output() dataDeleted = new EventEmitter<any>();
  @Output() selectedRowsSubject = new EventEmitter<any>();
  @Output() dataUpdated = new EventEmitter<DataUpdatedOutput>();

  @ViewChildren("headerInput") headerInputs: QueryList<EditableTableDynamicInputComponent> | undefined;
  @ViewChildren("tableDataInput") tableDataInputs: QueryList<EditableTableDynamicInputComponent> | undefined;

  ngOnInit() {
    console.log(this.tableHeaders);
  }

  ngAfterViewInit() {
    this.selectedRowsInput.forEach((row) => {
      if (!this.isSelected(row)) {
        this.selectedRows.push(row);
      }
    });
  }


  updateValue(event: any, rowIndex: number, header: EditableTableHeader) {
    const value = event.target ? (event.target as HTMLInputElement).value : event[header.fieldInfo?.lookupDetails?.bindValue || "id"];

    if (this.tableData[rowIndex][header.label] === value)
      return;

    this.tableData[rowIndex][header.label] = value;
    this.dataUpdated.emit({
      key: header.label,
      rowInfo: this.tableData[rowIndex],
      index: rowIndex,
      value: this.tableData,
      inputElement: event.target as HTMLInputElement
    });
  }

  getColumnValue(obj: any, header: EditableTableHeader, formatData: boolean): any {
    const path = header.label;
    const rawValue = !path.includes(".")
      ? obj[path]
      : path.split(".").reduce((acc, part) => acc && acc[part], obj);

    return formatData ? CommonUtils.formatData(rawValue, header?.fieldInfo?.dataType || "") : rawValue;
  }

  updateColumnValue(header: EditableTableHeader, event: any) {
    const value = event.target ? (event.target as HTMLInputElement).value : event;
    this.columnValues[header.label] = { header, value };
  }

  applyAllAllowed(): boolean {
    return this.selectedRows.length > 0 && this.selectedCols.length > 0;
  }

  bulkDeleteAllowed(): boolean {
    return this.bulkDeletionAllowed && this.selectedRows.length > 0;
  }

  toggleAllRecordsOfCurrentPageSelections(isChecked: boolean) {
    if (isChecked) {
      this.tableData?.forEach((item) => {
        if (!this.isSelected(item)) {
          this.selectedRows.push(item);
        }
      });
    } else {
      this.tableData?.forEach((item) => {
        this.selectedRows = this.selectedRows.filter((i) => i[this.uniqueId] !== item[this.uniqueId]);
      });
    }
  }

  isSelected(item: any): boolean {
    if (this.allTableRecordsSelected) {
      return true;
    } else {
      const recordIdx = this.selectedRows.findIndex((x) => x[this.uniqueId] === item[this.uniqueId]);
      if (recordIdx !== -1) {
        return true;
      } else {
        return false;
      }
    }
  }

  toggleSelection(item: any) {
    if (!this.isSelected(item)) {
      this.selectedRows.push(item);
    } else {
      this.selectedRows = this.selectedRows.filter((i) => i[this.uniqueId] !== item[this.uniqueId]);
    }
    this.selectedRowsSubject.emit(this.selectedRows);
  }

  isAllRecordsOfCurrentPageSelected(): boolean {
    return (
      this.allTableRecordsSelected ||
      (this.tableData?.length > 0 &&
        this.tableData?.every((item: any) =>
          this.isSelected(item)
        ))
    );
  }

  isSelectedColumn(selectedColumnLabel: string): boolean {
    return this.selectedCols.includes(selectedColumnLabel);
  }


  selectColumns(selectedColumnLabel: string) {
    if (!this.isSelectedColumn(selectedColumnLabel)) {
      this.selectedCols.push(selectedColumnLabel);
    } else {
      this.selectedCols = this.selectedCols.filter((i) => i !== selectedColumnLabel);
    }

    console.log(this.selectedCols);
  }

  applyEnteredValueForSelectedRows() {
    this.selectedRows.forEach((row) => {
      this.selectedCols.forEach((col) => {
        if (this.columnValues[col]?.value === undefined)
          return;

        //apply lookup value
        if (this.columnValues[col].header?.hasLookup) {
          this.applyLookupValues(col, row);
          return;
        }

        row[col] = this.columnValues[col].value;
      });
    });

    this.cancelSelection();
  }

  private applyLookupValues(col: string | number, row: { [x: string]: any; }) {
    this.tableDataInputs?.forEach(input => {
      if (input.autocompleteDropdown && this.isSelected(input.row)) {
        input.autocompleteDropdown.select(this.columnValues[col].value);
      }
    });
    row[col] = this.columnValues[col].value[this.columnValues[col].header?.fieldInfo?.lookupDetails?.bindValue];
  }

  cancelSelection() {
    this.selectedCols = [];
    this.selectedRows = [];

    this?.headerInputs?.forEach(input => {
      if (input.autocompleteDropdown) {
        input.autocompleteDropdown.searchTerm = "";
        return;
      }
      input.value = "";
    });
  }

  deleteRecord(data?: any, rowIndex?: number) {
    if (data !== undefined && rowIndex !== undefined) {
      this.dataDeleted.emit({ rowIndex, record: data });
    }
  }

  deleteSelectedRows() {
    this.dataDeleted.emit(this.selectedRows);
    this.cancelSelection();
  }

  isCellEditable(header: EditableTableHeader, row: any) {
    const conditionalEditable = header?.conditionalEditable ?? false;
    const dependentValue = header?.dependsOn ? row[header.dependsOn] : true;
    return conditionalEditable && dependentValue;
  }

  toggleDependency(header: EditableTableHeader, row: any) {
    if (!header || !header.dependsOn) return;

    // Toggle the dependent field value
    row[header?.dependsOn] = !row[header?.dependsOn];

    console.log("Toggled dependency:", row[header?.dependsOn]);

    if (!row[header?.dependsOn]) {
      //row[header.label] = "";
    }
  }

  isCellDisabled(header: EditableTableHeader, row: any) {
    const conditionalEditable = header?.conditionalEditable ?? false;
    const dependentValue = header?.dependsOn ? row[header.dependsOn] : true;
    const cellDisabled = conditionalEditable && !dependentValue;
    return this.disableTable || header.fieldInfo?.disable || row.disabled || cellDisabled;
  }
}
